import {
  collection, getDocs, query, where,
  doc, updateDoc, deleteDoc, setDoc,
} from 'firebase/firestore';
import {
  ref as storageRef, uploadBytes, getDownloadURL, deleteObject,
} from 'firebase/storage';
import { createUserWithEmailAndPassword, signOut } from 'firebase/auth';
import { db, storage, secondaryAuth } from '../firebase/firebase';
import { SellerModel } from '../model/sellerModel';

const COL = 'Sellers';
const colRef = collection(db, COL);

// ── Storage helpers ──────────────────────────────────────────────

export async function uploadSellerImage(file: File, uid: string): Promise<string> {
  const path = `sellers/${uid}_${file.name}`;
  const imgRef = storageRef(storage, path);
  await uploadBytes(imgRef, file);
  return getDownloadURL(imgRef);
}

export async function deleteStorageUrl(url: string): Promise<void> {
  if (!url || !url.includes('firebasestorage')) return;
  try {
    const match = url.match(/\/o\/(.+?)\?/);
    if (!match) return;
    await deleteObject(storageRef(storage, decodeURIComponent(match[1])));
  } catch (e) {
    console.warn('Storage delete skipped:', e);
  }
}

// ── CRUD ─────────────────────────────────────────────────────────

export const fetchSellers = async (activeOnly: boolean | null = null): Promise<(SellerModel & { docId: string })[]> => {
  try {
    const q = activeOnly === null ? colRef : query(colRef, where('active', '==', activeOnly));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(d => ({ docId: d.id, ...(d.data() as SellerModel) }));
  } catch (error) {
    console.error('Error fetching sellers:', error);
    return [];
  }
};

export const searchSellers = async (searchTerm: string): Promise<(SellerModel & { docId: string })[]> => {
  if (!searchTerm) return [];
  const q = query(colRef, where('name', '>=', searchTerm), where('name', '<=', searchTerm + '\uf8ff'));
  const snapshot = await getDocs(q);
  return snapshot.docs.map(d => ({ docId: d.id, ...(d.data() as SellerModel) }));
};

/** Register seller with Firebase Auth (secondary app) + Firestore */
export const registerSeller = async (
  email: string,
  password: string,
  sellerData: Omit<SellerModel, 'uid' | 'createdAt' | 'updatedAt'> & { image?: string },
): Promise<string> => {
  const credential = await createUserWithEmailAndPassword(secondaryAuth, email, password);
  const uid = credential.user.uid;
  await signOut(secondaryAuth);

  const now = new Date().toISOString();
  await setDoc(doc(db, COL, uid), {
    ...sellerData,
    uid,
    email,
    createdAt: now,
    updatedAt: now,
  });
  return uid;
};

export const updateSeller = async (docId: string, updateData: Partial<SellerModel> & { image?: string }) => {
  try {
    await updateDoc(doc(db, COL, docId), { ...updateData, updatedAt: new Date().toISOString() });
  } catch (error) {
    console.error('Error updating seller:', error);
    throw error;
  }
};

export const deleteSeller = async (docId: string, imageUrl?: string): Promise<void> => {
  await deleteDoc(doc(db, COL, docId));
  if (imageUrl) await deleteStorageUrl(imageUrl);
};
